import React from 'react';
import PropTypes from 'prop-types';
import { Table, Dropdown, Button, ToastContext, TableCellDropdown } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useNotifications, Notification } from '../../components/notifications/notificationsProvider';
import { Modal } from 'semantic-ui-react';
import Auth from '../../utils/auth';

function DeleteBatchModal({ showDeleteModal, onCancel, onAccept }) {
    const { t } = useTranslation();
    return (
        <Modal open={showDeleteModal}>
            <Modal.Header>{t('ticket.partners.batch.booking.delete.title_modal')}</Modal.Header>
            <Modal.Content>{t('ticket.partners.batch.booking.delete.body_modal')}</Modal.Content>
            <Modal.Actions>
                <Button primary style={{ marginRight: '20px' }} onClick={onAccept} label={t('ticket.common.cta.yes')} />
                <Button onClick={onCancel} label={t('ticket.common.cta.no')} />
            </Modal.Actions>
        </Modal>
    );
}

DeleteBatchModal.propTypes = {
    showDeleteModal: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onAccept: PropTypes.func.isRequired
};

export default function PartnerBatchBookingsRow({ batchBooking, onDelete, organizationId, partnerId, onAction, onClick, partner }) {
    const [price, setPrice] = React.useState('-');
    const [rateTitle, setRateTitle] = React.useState(null);
    const [date, setDate] = React.useState(null);
    const [event, setEvent] = React.useState('-');
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const { addNotification } = useNotifications();

    const fillUpBatch = React.useCallback(() => {
        onAction({
            description: t('ticket.partners.batch.actions.fillUp.detail.modalDescription', {
                name: batchBooking.name
            }),
            run: () => {
                return batchBooking
                    .fillUp({ organizationId, partnerId })
                    .then(() => {
                        toast.success(t('ticket.partners.batch.booking.fillup.success'));
                    })
                    .catch(e => {
                        console.error(e);
                        toast.error(t('ticket.partners.batch.booking.fillup.error'));
                    });
            }
        });
    }, [batchBooking, organizationId]);

    const deleteBatch = React.useCallback(() => {
        return batchBooking
            .delete({ organizationId: organizationId, partnerId })
            .then(() => {
                toast.success(t('ticket.partners.batch.booking.delete.success'));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.partners.batch.booking.delete.error'));
            })
            .finally(() => onDelete());
    }, [batchBooking, onDelete, organizationId, partnerId]);

    const freeBookings = React.useCallback(() => {
        onAction({
            title: t('ticket.partners.batch.booking.detail.free_confirm_title'),
            description: t('ticket.partners.batch.booking.detail.free_confirm_description'),
            run: () => {
                return batchBooking
                    .freeRemainingBookings({
                        organizationId: organizationId,
                        partnerId: partnerId,
                        rates: []
                    })
                    .then(response => {
                        addNotification(
                            new Notification('async_actions', 'free_remaining_bookings', 'partners', response?.response?.task_id, 'ticket.partners.batch.booking.detail.')
                        );
                        toast.info(t('ticket.manager.attendees.action.generic.toast.async'));
                    })
                    .catch(error => {
                        toast.error(t('ticket.partners.batch.booking.detail.error'));
                    });
            }
        });
    }, [batchBooking, organizationId, partnerId]);

    React.useEffect(() => {
        if (batchBooking) {
            setRateTitle(batchBooking.booking_rate.rate_title);
            setPrice(batchBooking.booking_rate.new_price);
            setEvent(batchBooking.booking_inventory);
        }
        let myDate = new Date(batchBooking.created);
        setDate(myDate.getDate() + '/' + (myDate.getMonth() + 1) + '/' + myDate.getFullYear());
    }, [setPrice]);

    if (!batchBooking) {
        return null;
    }
    return (
        <>
            <DeleteBatchModal
                showDeleteModal={showDeleteModal}
                onAccept={() => {
                    deleteBatch();
                    setShowDeleteModal(false);
                }}
                onCancel={() => setShowDeleteModal(false)}
            />
            <Table.Tr onClick={() => onClick(batchBooking.id)}>
                <Table.Td>{batchBooking.name}</Table.Td>
                <Table.Td>{rateTitle}</Table.Td>
                <Table.Td>
                    {batchBooking.available} / {batchBooking.total}
                </Table.Td>
                <Table.Td>{price}</Table.Td>
                <Table.Td>{date}</Table.Td>
                <Table.Td>{event}</Table.Td>
                <TableCellDropdown disabled={partner?.isDeleted()}>
                    {Boolean(batchBooking.available > 0) && (
                        <>
                            <Dropdown.Item item={t('ticket.partners.batch.booking.free.option_label', { count: batchBooking.available })} onClick={freeBookings} />
                            <Dropdown.Line />
                        </>
                    )}
                    {/* <Dropdown.Item item={t('ticket.partners.batch.booking.duplicateBatch')} onClick={() => console.warn("copy")} disabled={true} /> */}
                    <Dropdown.Item item={t('ticket.partners.batch.booking.deleteBatch')} disabled={batchBooking.taken !== 0} onClick={() => setShowDeleteModal(true)} deleteItem />
                    <Auth allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.PARTNER']}>
                        <Dropdown.Item
                            item={<span>{t('ticket.partners.batch.booking.fillUpBatch')}</span>}
                            disabled={batchBooking.taken === batchBooking.total}
                            onClick={fillUpBatch}
                        />
                    </Auth>
                </TableCellDropdown>
            </Table.Tr>
        </>
    );
}

PartnerBatchBookingsRow.propTypes = {
    batchBooking: PropTypes.object.isRequired,
    onDelete: PropTypes.func.isRequired,
    onFreeRemaningBookings: PropTypes.func.isRequired,
    organizationId: PropTypes.string.isRequired,
    partnerId: PropTypes.string.isRequired
};
